import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AutosaveIndicator,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { parseFormSchema } from "@/lib/formSchema";
import { normalizeDealSubmissionFinancialSchema } from "@/lib/dealSubmissionFinancialLabels";
import type { FormSchema } from "@/lib/formSchema";
import {
  PortalFormRenderer,
  findFirstMissingRequired,
  findFirstFormatError,
  type PortalFormData,
} from "../../components/PortalFormRenderer";
import { dealSubmissionApi } from "../../api/dealSubmission";
import { PortalApiError } from "../../api/client";
import { DocumentRequestList } from "./DocumentsSection";
import type {
  DealSubmissionAiFeedbackResult,
  DealSubmissionDocumentDto,
  DealSubmissionDocumentRequestDto,
  DealSubmissionSectionDto,
  DealSubmissionSectionStateDto,
} from "../../api/types";
import {
  normalizeFeedback,
  parseSectionFeedback,
  SectionFeedbackPanel,
} from "./SectionFeedbackPanel";
import {
  evaluationStatusLabel,
  evaluationStatusTone,
  formatOutstandingEvaluationMessage,
  formatSectionPrerequisiteMessage,
  outstandingDocumentEvaluationNames,
  outstandingEvaluationNames,
} from "./evaluationFreshness";
import {
  passthroughEvaluationTracker,
  sectionEvalKey,
  type EvaluationTracker,
} from "./evaluationTracker";

// Wizard data-entry page. Mirrors FormItemSurface's autosave (900 ms
// debounce → PUT /sections/{sectionId} with Status=InProgress). Marks
// the section Complete only when client-side required-field validation
// passes and the user clicks "Save & continue".
//
// FL-13 guard — Deal Submission DataEntry schemas must not include
// fileattach fields (the template builder rejects them). If one ever
// sneaks through, we strip it so the broker never sees a file control
// on a data page; all document collection routes through DocumentsSection
// → DocumentRequestUploader.

const AUTOSAVE_DELAY_MS = 900;

interface DataEntrySectionProps {
  submissionId: string;
  section: DealSubmissionSectionDto;
  sectionState: DealSubmissionSectionStateDto | null;
  editable: boolean;
  // Runner-owned in-flight evaluation tracker — survives section navigation so
  // a running evaluation can't be re-fired and its result still displays.
  // Optional: defaults to an inline passthrough when rendered outside the runner.
  evaluation?: EvaluationTracker;
  // Runner callback fired with the authoritative section state every save
  // returns, so the runner's cached payload stays fresh and a remount (on
  // navigation) re-initialises from the SAVED answers, not a stale snapshot.
  // Optional: a no-op when rendered outside the runner (e.g. unit tests).
  onSectionStatePersisted?: (state: DealSubmissionSectionStateDto) => void;
  // RECON→FIX — document requests the admin authored under THIS section
  // (may be empty). Rendered inline so they're visible + uploadable here,
  // not stranded as un-uploadable submit blockers. Uploaded docs + the
  // refresh callback come through so the inline uploaders work like the
  // dedicated Documents section.
  documentRequests: DealSubmissionDocumentRequestDto[];
  documents: DealSubmissionDocumentDto[];
  onDocumentsChanged: () => Promise<void>;
  commentPanel?: ReactNode;
  onContinue: () => Promise<void>;
  onExit: () => void;
}

export function DataEntrySection({
  submissionId,
  section,
  sectionState,
  editable,
  evaluation = passthroughEvaluationTracker,
  onSectionStatePersisted,
  documentRequests,
  documents,
  onDocumentsChanged,
  commentPanel,
  onContinue,
  onExit,
}: DataEntrySectionProps) {
  const schema = useMemo<FormSchema | null>(() => {
    const parsed = parseFormSchema(section.formSchemaJson);
    if (!parsed) return null;
    // FL-13 defense-in-depth — strip any fileattach field that survived
    // the admin builder. The user collects every document through the
    // dedicated upload widget on Documents sections.
    const cleaned = parsed.fields.filter((f) => f.type !== "fileattach");
    return normalizeDealSubmissionFinancialSchema({ ...parsed, fields: cleaned });
  }, [section.formSchemaJson]);

  const [data, setData] = useState<PortalFormData>(() => {
    if (!sectionState?.savedDataJson) return {};
    try {
      const parsed = JSON.parse(sectionState.savedDataJson) as unknown;
      return parsed && typeof parsed === "object" ? (parsed as PortalFormData) : {};
    } catch {
      return {};
    }
  });
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const evalKey = sectionEvalKey(section.id);
  // Advisory per-section AI feedback. Prefer a result the runner-level tracker
  // retained for this section (so it shows immediately on return, even if the
  // run finished while the broker was on another section and the persisted
  // reload hasn't landed yet); otherwise fall back to the persisted scan. Set
  // optimistically on a successful run too, for the standalone (no-tracker) case.
  const trackerResult = evaluation.result(evalKey);
  const [feedback, setFeedback] = useState<DealSubmissionAiFeedbackResult | null>(
    () => trackerResult ?? parseSectionFeedback(sectionState?.aiFeedbackJson ?? null),
  );
  useEffect(() => {
    setFeedback(trackerResult ?? parseSectionFeedback(sectionState?.aiFeedbackJson ?? null));
  }, [trackerResult, sectionState?.aiFeedbackJson]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<string>(sectionState?.savedDataJson ?? "");

  const flushSave = useCallback(
    async (payload: PortalFormData, status: "InProgress" | "Complete"): Promise<void> => {
      const blob = JSON.stringify(payload);
      if (blob === lastSentRef.current && status === "InProgress") return;
      setSaveState("saving");
      try {
        const saved = await dealSubmissionApi.saveSection(submissionId, section.id, {
          savedDataJson: blob,
          status,
        });
        lastSentRef.current = blob;
        setLastSavedAt(new Date().toISOString());
        setSaveState("saved");
        // Push the saved state up so the runner's cached payload (which a
        // remount re-reads) reflects these answers instead of the stale
        // open-time snapshot.
        onSectionStatePersisted?.(saved);
      } catch (err) {
        setSaveState("error");
        const msg =
          err instanceof PortalApiError ? err.message
            : err instanceof Error ? err.message
            : "Couldn't save your answers.";
        setErrorMsg(msg);
        throw err;
      }
    },
    [submissionId, section.id, onSectionStatePersisted],
  );

  useEffect(() => {
    if (!editable) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void flushSave(data, "InProgress").catch(() => {
        /* error state already surfaced */
      });
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [data, editable, flushSave]);

  // The wizard remounts each section on navigation (SectionPane is keyed by
  // id), and the debounce timer above is cleared on unmount — so an edit made
  // within the 900 ms window before the broker switches steps would be dropped.
  // Flush any pending (un-sent) change on unmount. Refs keep the cleanup
  // reading the latest values while the effect itself runs only once.
  const latestSave = useRef({ data, editable, flushSave });
  useEffect(() => {
    latestSave.current = { data, editable, flushSave };
  });
  useEffect(
    () => () => {
      const { data: latest, editable: canEdit, flushSave: save } = latestSave.current;
      if (!canEdit) return;
      if (JSON.stringify(latest) === lastSentRef.current) return;
      void save(latest, "InProgress").catch(() => {
        /* best-effort: the section is unmounting, errors surface on next load */
      });
    },
    [],
  );

  // The local mutation's pending drives the spinner while mounted; the tracker
  // covers the case where the run is still in flight after navigating away and
  // back.
  const evaluate = useSubmittingMutation(async () => {
    // The tracker de-dupes (a second click while running is a no-op) and
    // reloads the submission on completion so the persisted feedback renders
    // even if the broker navigated away mid-run.
    await evaluation.run(evalKey, async () => {
      try {
        await flushSave(data, "InProgress");
        const result = await dealSubmissionApi.evaluateSection(submissionId, section.id);
        const fb = normalizeFeedback(result);
        setFeedback(fb);
        return fb;
      } catch (err) {
        const msg =
          err instanceof PortalApiError ? err.message
            : err instanceof Error ? err.message
            : "Couldn't evaluate this section.";
        setErrorMsg(msg);
        return null;
      }
    });
  });
  const evaluating = evaluate.pending || evaluation.isEvaluating(evalKey);

  const saveAndContinue = useSubmittingMutation(async () => {
    try {
      // Empty schema still saves Complete so section state advances; the
      // validator treats no-fields as satisfied.
      await flushSave(data, "Complete");
      await onContinue();
    } catch {
      /* surfaced */
    }
  });

  const saveAndExit = useSubmittingMutation(async () => {
    try {
      await flushSave(data, "InProgress");
      onExit();
    } catch {
      /* surfaced */
    }
  });

  const busy = saveAndContinue.pending || saveAndExit.pending;

  // RECON→FIX — a required document authored under this section must be
  // uploaded before the section can be marked Complete, so "Done" can't be
  // claimed while a required doc is still missing.
  const missingRequiredDoc = documentRequests
    .filter((r) => r.required)
    .find((r) => !documents.some((d) => d.documentRequestId === r.id));
  // Computed once at render so both the continue gate and the Evaluate gate
  // share one definition of "the section's required input is satisfied".
  const requiredFieldMissing = schema ? findFirstMissingRequired(schema, data) : null;
  const formatError = schema ? findFirstFormatError(schema, data) : null;
  // The broker can scroll past an empty form to the Evaluate & Feedback button;
  // lock it until the required input is present so they can't request a review
  // of nothing (the section roll-up would have no answers to assess).
  const evaluateBlockedReason = requiredFieldMissing
    ? `Fill in "${requiredFieldMissing.label}" before evaluating.`
    : formatError
      ? `"${formatError.field.label}" — ${formatError.message}`
      : missingRequiredDoc
        ? `Upload "${missingRequiredDoc.label}" before evaluating.`
        : null;
  const outstandingEvaluations = outstandingEvaluationNames(
    section,
    sectionState,
    documentRequests,
    documents,
  );
  const evaluationMessage = formatOutstandingEvaluationMessage(outstandingEvaluations);
  // Section feedback rolls up the per-document reviews, so it can't run until
  // every document in this section is evaluated. Gate the button (the server
  // rejects it too) and explain why.
  const sectionDocPrereqs = outstandingDocumentEvaluationNames(documentRequests, documents);
  const sectionPrereqMessage = formatSectionPrerequisiteMessage(sectionDocPrereqs);
  const sectionEvaluationStatus = section.feedbackEnabled
    ? sectionState?.aiFeedbackStatus ?? "NotEvaluated"
    : "NotApplicable";

  function handleContinue() {
    setErrorMsg(null);
    if (requiredFieldMissing) {
      setErrorMsg(`Please fill in "${requiredFieldMissing.label}" before continuing.`);
      return;
    }
    if (formatError) {
      setErrorMsg(`"${formatError.field.label}" — ${formatError.message}`);
      return;
    }
    if (missingRequiredDoc) {
      setErrorMsg(`Please upload "${missingRequiredDoc.label}" before continuing.`);
      return;
    }
    if (evaluationMessage) {
      setErrorMsg(evaluationMessage);
      return;
    }
    void saveAndContinue.submit();
  }

  function handleEvaluate() {
    setErrorMsg(null);
    void evaluate.submit();
  }

  function handleSaveAndExit() {
    setErrorMsg(null);
    void saveAndExit.submit();
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid={`section-data-${section.order}`}>
      <header>
        <h2 className="text-h3 font-display text-onyx m-0">{section.title}</h2>
        {section.instructions && (
          <p className="text-small text-gray-mid mt-space-2 whitespace-pre-line">
            {section.instructions}
          </p>
        )}
      </header>

      {commentPanel}

      {schema && schema.fields.length > 0 ? (
        <PortalFormRenderer
          schema={schema}
          value={data}
          onChange={setData}
          disabled={!editable || busy}
        />
      ) : documentRequests.length === 0 ? (
        <p className="text-small text-gray-mid m-0">
          This section has no questions to answer.
        </p>
      ) : null}

      {/* RECON→FIX — documents authored under this section render inline
          so every required doc is visible + uploadable in the section it
          belongs to (not hidden while still gating submit). */}
      {documentRequests.length > 0 && (
        <div className="flex flex-col gap-space-2">
          <h3 className="text-h5 font-display text-onyx m-0">Supporting documents</h3>
          <DocumentRequestList
            submissionId={submissionId}
            requests={documentRequests}
            documents={documents}
            editable={editable}
            evaluation={evaluation}
            onChanged={onDocumentsChanged}
            emptyMessage="No documents requested in this section."
          />
        </div>
      )}

      {editable && section.feedbackEnabled && (
        <div>
          <div
            className={`text-caption font-medium mb-space-2 ${evaluationStatusTone(sectionEvaluationStatus)}`}
            data-testid="section-evaluation-status"
          >
            {evaluationStatusLabel(sectionEvaluationStatus)}
          </div>
          <SubmitButton
            variant="secondary"
            onClick={handleEvaluate}
            pending={evaluating}
            pendingLabel="Evaluating"
            disabled={busy || sectionDocPrereqs.length > 0 || Boolean(evaluateBlockedReason)}
            data-testid="section-evaluate"
          >
            Evaluate &amp; Feedback
          </SubmitButton>
          {(evaluateBlockedReason || sectionPrereqMessage) && (
            <p
              className="text-caption text-status-error m-0 mt-space-2"
              role="status"
              data-testid="section-evaluate-blocked"
            >
              {evaluateBlockedReason ?? sectionPrereqMessage}
            </p>
          )}
          {evaluating && (
            <p className="text-caption text-gray-mid m-0 mt-space-2" role="status">
              This can take a few minutes — you can keep working on other sections while it runs.
            </p>
          )}
        </div>
      )}

      {feedback && <SectionFeedbackPanel feedback={feedback} context="section" />}

      {evaluationMessage && (
        <p className="text-small text-status-error m-0" role="alert" data-testid="evaluation-gate-message">
          {evaluationMessage}
        </p>
      )}

      {errorMsg && <p className="text-small text-status-error m-0" role="alert">{errorMsg}</p>}

      {editable && (
        <div className="sticky bottom-0 z-10 -mx-space-1 flex items-center justify-between gap-space-3 flex-wrap border-t border-border-light bg-white-warm/95 px-space-1 py-space-3 backdrop-blur-sm">
          <AutosaveIndicator
            state={saveState}
            lastSavedAt={lastSavedAt}
            idleHint={
              sectionState?.savedDataJson
                ? "Your last answers are restored."
                : "Your answers save automatically."
            }
            data-testid="autosave-indicator"
          />
          <div className="flex items-center gap-space-2 flex-wrap">
            <SubmitButton
              variant="secondary"
              onClick={handleSaveAndExit}
              pending={saveAndExit.pending}
              pendingLabel="Saving…"
              disabled={busy}
              data-testid="wizard-save-and-exit"
            >
              Save &amp; exit
            </SubmitButton>
            <SubmitButton
              onClick={handleContinue}
              pending={saveAndContinue.pending}
              pendingLabel="Saving…"
              disabled={busy || outstandingEvaluations.length > 0}
              data-testid="wizard-save-and-continue"
            >
              Save &amp; continue
            </SubmitButton>
          </div>
          <SubmittingHint pending={busy} className="w-full" />
        </div>
      )}
    </div>
  );
}

export type { DataEntrySectionProps };
